import { ServerError } from '@apollo/client';
import * as R from 'ramda';

import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

import { ModalStep, ModalStepType } from './MoveElementModalContext';

export const MODAL_STEPS: { [key: string]: ModalStepType } = {
  [ModalStep.ELEMENT]: {
    value: ModalStep.ELEMENT,
    title: 'Move',
  },
  [ModalStep.DESTINATION]: {
    value: ModalStep.DESTINATION,
    title: 'Select Destination',
  },
  [ModalStep.CONFIRM]: {
    value: ModalStep.CONFIRM,
    title: 'Confirm',
  },
};

export const getModalStepsArray = () => {
  const steps = [ModalStep.ELEMENT, ModalStep.DESTINATION, ModalStep.CONFIRM];
  return steps.map(step => MODAL_STEPS[step]);
};

// Evaluates whether the current step should be disabled
// The current step will be disabled if any previous step is also disabled
export const isCurrentStepDisabled = ({
  STEPS_ARRAY,
  activeStep,
  element,
  destinationAsset,
}) => {
  // Map of all disable validation checks per given modal step
  const stepDisabledRules = {
    [ModalStep.ELEMENT]: R.isNil(element),
    [ModalStep.DESTINATION]: R.isNil(element) || R.isNil(destinationAsset),
    [ModalStep.CONFIRM]: R.isNil(element) || R.isNil(destinationAsset),
  };

  const currentStepIndex = R.findIndex(
    R.propEq(activeStep, 'value'),
    STEPS_ARRAY,
  );

  // Array of all steps up to the current step
  const stepsToValidate = R.dropLast(
    STEPS_ARRAY.length - currentStepIndex - 1,
    STEPS_ARRAY,
  );

  // Creates an array of validations, denoting whether the step at the given
  // index should be disabled or not
  const validatedSteps = stepsToValidate.map((step: ModalStepType) => {
    return stepDisabledRules[step.value];
  });

  // If any previous step (or current) step are disabled, the step is disabled
  return R.any(R.equals(true), validatedSteps);
};

// Element errors are passed in a very specific place within the GQL errors array
// This method pulls them out, given the proper error code of 422.
export const getElementErrorsFromError = (error: any) => {
  if (!ServerError.is(error)) {
    return {};
  }

  const body = R.tryCatch(JSON.parse, R.always(null))(error.bodyText);

  const errorsArray = R.pathOr([], ['errors'], body);

  if (isNilOrEmpty(errorsArray)) {
    return {};
  }

  const validationError = R.find(
    item =>
      item?.extensions?.code === 422 && R.has('errorBody')(item?.extensions),
    errorsArray,
  );

  const elementErrors = R.pathOr(
    {},
    ['extensions', 'errorBody', 'elementErrors'],
    validationError,
  );

  return elementErrors;
};
